/**
 * Feedback — the Feedback hub (ADR-028 Phase 2).
 *
 * Polls GET /api/fleet/feedback every 15s and renders the loop so the team can
 * see it working: grade pulse + 1–5 distribution + per-agent, a recent reactions
 * feed, the latest retros, and active themes. NEVER fabricates — empty sections
 * say so. Read-only.
 */
import { useEffect, useState, useCallback } from 'react'
import { api, UnauthorizedError } from '../api/client'
import type { FleetFeedback } from '../api/client'
import { relativeTime } from '../utils/relativeTime'

const POLL_INTERVAL_MS = 15_000

function StatCard({ label, value, sub, warn }: { label: string; value: string; sub?: string; warn?: boolean }) {
  return (
    <div className={`rounded-lg border bg-white p-5 ${warn ? 'border-amber-300' : 'border-gray-200'}`}>
      <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">{label}</p>
      <p className={`text-2xl font-bold mt-1 ${warn ? 'text-amber-700' : 'text-gray-900'}`}>{value}</p>
      {sub && <p className="text-xs text-gray-400 mt-1">{sub}</p>}
    </div>
  )
}

function Stars({ n }: { n: number }) {
  const full = Math.round(n)
  return <span className="text-amber-500" title={`${n} / 5`}>{'★'.repeat(full)}<span className="text-gray-300">{'★'.repeat(5 - full)}</span></span>
}

function StatusBadge({ status }: { status: string }) {
  const map: Record<string, string> = {
    merged: 'bg-green-100 text-green-800',
    proposed: 'bg-blue-100 text-blue-800',
    delegated: 'bg-amber-100 text-amber-800',
    open: 'bg-gray-100 text-gray-600',
    declined: 'bg-red-100 text-red-800',
    reverted: 'bg-red-100 text-red-800',
  }
  return <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${map[status] ?? 'bg-gray-100 text-gray-600'}`}>{status}</span>
}

function Distribution({ dist }: { dist: Record<string, number> }) {
  const max = Math.max(1, ...Object.values(dist))
  return (
    <div className="space-y-1">
      {[5, 4, 3, 2, 1].map((s) => {
        const n = dist[String(s)] ?? 0
        return (
          <div key={s} className="flex items-center gap-2 text-xs">
            <span className="w-3 text-gray-500">{s}</span>
            <div className="flex-1 bg-gray-100 rounded h-3">
              <div className="bg-amber-400 h-3 rounded" style={{ width: `${(n / max) * 100}%` }} />
            </div>
            <span className="w-8 text-right text-gray-500">{n}</span>
          </div>
        )
      })}
    </div>
  )
}

export function Feedback() {
  const [data, setData] = useState<FleetFeedback | null>(null)
  const [error, setError] = useState<string | null>(null)

  const fetchFeedback = useCallback(async () => {
    try {
      setData(await api.getFeedback())
      setError(null)
    } catch (err) {
      if (err instanceof UnauthorizedError) setError('Session expired. Please re-authenticate.')
      else setError(err instanceof Error ? err.message : 'Failed to load feedback.')
    }
  }, [])

  useEffect(() => {
    void fetchFeedback()
    const interval = setInterval(() => { void fetchFeedback() }, POLL_INTERVAL_MS)
    return () => clearInterval(interval)
  }, [fetchFeedback])

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold text-gray-900">Feedback</h1>
        <p className="text-sm text-gray-500 mt-1">What the fleet is learning — grades, reactions, retros, and themes.</p>
      </div>

      {error && (
        <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">{error}</div>
      )}
      {!data && !error && <div className="text-sm text-gray-400 animate-pulse">Loading feedback…</div>}

      {data && (
        <>
          {/* Grade pulse */}
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-3">
            <StatCard
              label="Avg grade (24h)"
              value={data.grades.avg_stars_24h !== null ? `${data.grades.avg_stars_24h} ★` : '—'}
              sub={`${data.grades.count_24h} graded`}
            />
            <StatCard label="👍 Reactions (24h)" value={String(data.grades.reactions_24h)} sub="Slack reactions" />
            <div className="rounded-lg border border-gray-200 bg-white p-5">
              <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Grade distribution (7d)</p>
              <div className="mt-2"><Distribution dist={data.grades.distribution} /></div>
            </div>
          </div>

          {/* Per-agent + reactions feed */}
          <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
            <section className="bg-white rounded-lg border border-gray-200 overflow-hidden">
              <h2 className="px-5 py-3 border-b border-gray-100 text-sm font-semibold text-gray-700">By agent (7d)</h2>
              {data.grades.by_agent.length === 0
                ? <p className="px-5 py-4 text-sm text-gray-400">No grades yet.</p>
                : (
                  <table className="w-full text-sm">
                    <tbody>
                      {data.grades.by_agent.map((a) => (
                        <tr key={a.agent} className="border-b border-gray-50 last:border-0">
                          <td className="px-5 py-2 font-mono text-gray-800">{a.agent}</td>
                          <td className="px-5 py-2"><Stars n={a.avg_stars} /></td>
                          <td className="px-5 py-2 text-right text-gray-500">{a.count}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
            </section>

            <section className="bg-white rounded-lg border border-gray-200 overflow-hidden">
              <h2 className="px-5 py-3 border-b border-gray-100 text-sm font-semibold text-gray-700">Recent reactions</h2>
              {data.reactions.length === 0
                ? <p className="px-5 py-4 text-sm text-gray-400">No reactions yet — enable 👍 feedback (see runbook).</p>
                : (
                  <ul className="divide-y divide-gray-50">
                    {data.reactions.map((r, i) => (
                      <li key={i} className="px-5 py-2 text-sm flex items-center justify-between">
                        <span><Stars n={r.stars} /> <span className="text-gray-500">on</span> <span className="font-mono text-gray-700">{r.target_agent ?? '—'}</span></span>
                        <span className="text-xs text-gray-400">{relativeTime(r.created_at)}</span>
                      </li>
                    ))}
                  </ul>
                )}
            </section>
          </div>

          {/* Retros + themes */}
          <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
            <section className="bg-white rounded-lg border border-gray-200 overflow-hidden">
              <h2 className="px-5 py-3 border-b border-gray-100 text-sm font-semibold text-gray-700">Latest retros</h2>
              {data.retros.length === 0
                ? <p className="px-5 py-4 text-sm text-gray-400">No retros yet.</p>
                : (
                  <ul className="divide-y divide-gray-50">
                    {data.retros.map((r, i) => (
                      <li key={i} className="px-5 py-3 text-sm">
                        <p className="font-medium text-gray-700">{r.participant} <span className="text-xs text-gray-400">· {r.retro_date}</span></p>
                        {r.good.length > 0 && <p className="text-green-700 mt-1">+ {r.good.join('; ')}</p>}
                        {r.not_good.length > 0 && <p className="text-amber-700 mt-1">△ {r.not_good.join('; ')}</p>}
                      </li>
                    ))}
                  </ul>
                )}
            </section>

            <section className="bg-white rounded-lg border border-gray-200 overflow-hidden">
              <h2 className="px-5 py-3 border-b border-gray-100 text-sm font-semibold text-gray-700">Active themes</h2>
              {data.themes.length === 0
                ? <p className="px-5 py-4 text-sm text-gray-400">No themes yet.</p>
                : (
                  <ul className="divide-y divide-gray-50">
                    {data.themes.map((t, i) => (
                      <li key={i} className="px-5 py-2 text-sm flex items-center justify-between">
                        <span className="text-gray-800">{t.label}</span>
                        <span className="text-xs text-gray-400">
                          ×{t.occurrence_count}
                          {t.sentiment_negative > t.sentiment_positive ? ' · watch' : ''}
                        </span>
                      </li>
                    ))}
                  </ul>
                )}
            </section>
          </div>

          {/* What you taught → what changed (ADR-030 WS3) */}
          <section className="bg-white rounded-lg border border-gray-200 overflow-hidden">
            <h2 className="px-5 py-3 border-b border-gray-100 text-sm font-semibold text-gray-700">What you taught → what changed</h2>
            {data.changes.improvements.length === 0 && data.changes.self_edits.length === 0
              ? <p className="px-5 py-4 text-sm text-gray-400">No changes from feedback in the last 2 weeks.</p>
              : (
                <ul className="divide-y divide-gray-50">
                  {data.changes.improvements.map((c, i) => (
                    <li key={`imp-${i}`} className="px-5 py-2 text-sm flex items-center justify-between gap-3">
                      <span className="truncate"><span className="text-gray-400">improvement → </span><span className="font-mono text-gray-700">{c.owner_agent}</span> <span className="text-gray-600">“{c.verbatim}”</span></span>
                      <span className="flex items-center gap-2 shrink-0">
                        <StatusBadge status={c.status} />
                        {c.pr_url && <a href={c.pr_url} target="_blank" rel="noreferrer" className="text-blue-600 hover:underline text-xs">PR</a>}
                      </span>
                    </li>
                  ))}
                  {data.changes.self_edits.map((c, i) => (
                    <li key={`se-${i}`} className="px-5 py-2 text-sm flex items-center justify-between gap-3">
                      <span className="truncate"><span className="text-gray-400">self-edit → </span><span className="font-mono text-gray-700">{c.agent}</span> <span className="text-gray-600">{c.title}</span></span>
                      <span className="flex items-center gap-2 shrink-0">
                        <StatusBadge status={c.status} />
                        {c.pr_url && <a href={c.pr_url} target="_blank" rel="noreferrer" className="text-blue-600 hover:underline text-xs">PR</a>}
                      </span>
                    </li>
                  ))}
                </ul>
              )}
          </section>
        </>
      )}
    </div>
  )
}
